import styles from "./Draw.module.scss";
import { CardWrapper } from "../../components/CardWrapper/CardWrapper";
import { PageTitleBlock } from "../../components/PageTitleBlock/PageTitleBlock";
import { useSelector, useDispatch } from "../../hooks";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { getDraws } from "../../redux/draws/get-draws.handler";
import { DrawMemberStatus, DrawMethod, IDraw } from "./DrawItem";
import { DrawCardPassive } from "./draws/DrawCardPassive";
import { DrawCardConfirm } from "./draws/DrawCardConfirm";
import { DrawCardAvailable } from "./draws/DrawCardAvailable";
import { FirstDrawPopup } from "../../components/Popup/FirstDrawPopup/FirstDrawPopup";
import { JoinChannelPopup } from "../../components/Popup/JoinChannelPopup/JoinChannelPopup";
import { ChannelLink } from "../../components/Links/ChannelLink/ChannelLink";
import { DrawsTopBlock } from "./DrawsTopBlock/DrawsTopBlock";
import { useCurrentDraw } from "../../hooks/draw";
import { now } from "../../utils/time";
import { getPlatformText } from "../../utils/platform-text";

interface IDrawListPageProps {
    method: DrawMethod
}

export const DrawListPage: FC<IDrawListPageProps> = ({ method }) => {
    const posDrawId = useCurrentDraw();
    
    const [joinChannelPopup, setJoinChannelPopup] = useState(false);
    const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

    const isFirstLaunch = useSelector(store => store.user.info.isFirstLaunch);

    const draws = useSelector(store => store.draws.items);
    const filteredDraws = useMemo(
        () => draws.filter(draw => draw.method === method),
        [draws, method]
    );

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getDraws());
    }, [dispatch]);

    useEffect(() => {
        if (!posDrawId || !filteredDraws)
            return;

        const drawIndex = filteredDraws.findIndex(draw => draw.drawId === posDrawId.drawId);
        if (drawIndex !== -1)
            scrollToIndex(drawIndex);
    }, [filteredDraws, posDrawId])

    const getDrawCard = (draw: IDraw) => {
        switch (draw.userStatus) {
            case DrawMemberStatus.None:
                return <DrawCardAvailable draw={ draw } setJoinChannelPopup={ setJoinChannelPopup } />;

            case DrawMemberStatus.Candidate:
                return <DrawCardConfirm draw={ draw } />;

            case DrawMemberStatus.Member:
            case DrawMemberStatus.Expired:
            case DrawMemberStatus.Confirmed:
            case DrawMemberStatus.Winner:
                return <DrawCardPassive draw={ draw } />;
        }
    }

    const getJoinChannelPopup = () => {
        return isFirstLaunch
            ? <FirstDrawPopup onClose={ () => setJoinChannelPopup(false) } />
            : <JoinChannelPopup onClose={ () => setJoinChannelPopup(false) } />
    }

    const positionHandler = () => {
        const availableIndex = filteredDraws.findIndex(draw => draw.userStatus === DrawMemberStatus.None && now() < draw.operDate);
        scrollToIndex(availableIndex);        
    };

    const scrollToIndex = (index: number) => {
        if (index !== -1 && itemsRef.current[index]) {
            itemsRef.current[index]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }

    const isInterctive = () => method === DrawMethod.Interactive;

    const getDescription = () => {
        return (
            <>
                <span>Розыгрыш проходит { isInterctive() ? 'в' : ' автоматически. Результаты публикуются в' } </span>
                <ChannelLink caption={getPlatformText("channelIn")} />
            </>
        );
    }

    return (
        <>
            <div className={styles.cardWrapper}>
                <PageTitleBlock pageDescription={ getDescription() } />
                <DrawsTopBlock positionHandler={positionHandler} method={ method } />

                <CardWrapper>
                    { filteredDraws.length > 0 && filteredDraws                        
                        .map((draw: IDraw, index: number) => {
                            return (
                                <div
                                    key={index}
                                    style={{ textDecoration: "none", cursor: "pointer" }}
                                    ref={el => itemsRef.current[index] = el}
                                >
                                    { getDrawCard(draw) }
                                </div>
                            );
                    })}

                    { filteredDraws.length === 0 && <div className={styles.noDrawsMessage}>Пока розыгрышей нет</div> }
                </CardWrapper>
            </div>

            { joinChannelPopup && getJoinChannelPopup() }
        </>
    );
}